import { FormErrors, FormValues, ChampionPreference } from '../types/ChampionApplicationFormTypes';

// Common error messages (exact literals)
const ERROR_REQUIRED = 'This field is required.';
const ERROR_EMAIL = 'Enter a valid email address, such as name@email.com.';
const ERROR_MIN_LEN_4 = 'Length is too short - please enter at least 4 characters';
const ERROR_COMPLETE_FIELD = 'Complete this field.';
const ERROR_SELECT_ONE = 'Select at least one option.';
const ERROR_MIN_150 = 'Enter at least 150 characters.';
const ERROR_MAX_300 = 'Enter less than 300 characters.';
const ERROR_ORG_ACCOUNT_REQUIRED = 'Please use an org account to sign up as success champion.';

const MAX_EMAIL_LENGTH = 254;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SUCCESS_TRACKS: ChampionPreference[] = [
  'success_training_builder',
  'success_cug_leader',
  'success_coe_contributor',
];

type ValidationOptions = {
  accountIsManaged: boolean;
};

export const validateChampionApplication = (values: FormValues, options: ValidationOptions): FormErrors => {
  const errors: FormErrors = {};
  const minLen = 4;

  // 1. Personal info + contact
  if (!values.firstName.trim()) {
    errors.firstName = ERROR_REQUIRED;
  }

  if (!values.lastName.trim()) {
    errors.lastName = ERROR_REQUIRED;
  }

  if (!values.email.trim() || values.email.trim().length > MAX_EMAIL_LENGTH || !EMAIL_PATTERN.test(values.email.trim())) {
    errors.email = ERROR_EMAIL;
  }

  if (!values.communityProfileUrl.trim()) {
    errors.communityProfileUrl = ERROR_REQUIRED;
  }

  if (!values.linkedinUrl.trim()) {
    errors.linkedinUrl = ERROR_REQUIRED;
  }

  // 2. Address
  if (!values.street.trim()) {
    errors.street = ERROR_REQUIRED;
  } else if (values.street.trim().length < minLen) {
    errors.street = ERROR_MIN_LEN_4;
  }

  if (!values.city.trim()) {
    errors.city = ERROR_REQUIRED;
  } else if (values.city.trim().length < minLen) {
    errors.city = ERROR_MIN_LEN_4;
  }

  if (!values.stateProvince.trim()) {
    errors.stateProvince = ERROR_REQUIRED;
  } else if (values.stateProvince.trim().length < minLen) {
    errors.stateProvince = ERROR_MIN_LEN_4;
  }

  if (!values.postalCode.trim()) {
    errors.postalCode = ERROR_REQUIRED;
  } else if (values.postalCode.trim().length < minLen) {
    errors.postalCode = ERROR_MIN_LEN_4;
  }

  if (!values.country.trim()) {
    errors.country = ERROR_COMPLETE_FIELD;
  }

  // 3. Work & partner
  if (values.partnerType.length === 0) {
    errors.partnerType = ERROR_SELECT_ONE;
  }

  // 4. Products used
  if (values.products.length === 0) {
    errors.products = ERROR_SELECT_ONE;
  }
  if (values.products.includes('other') && !values.otherAtlassianProducts.trim()) {
    errors.otherAtlassianProducts = ERROR_REQUIRED;
  }

  // 5. Global motivation: 150–300 chars
  const why = values.whyBecomeChampion.trim();
  if (!why) {
    errors.whyBecomeChampion = ERROR_REQUIRED;
  } else if (why.length < 150) {
    errors.whyBecomeChampion = ERROR_MIN_150;
  } else if (why.length > 300) {
    errors.whyBecomeChampion = ERROR_MAX_300;
  }

  // 6. Top preference
  if (!values.championPreference) {
    errors.championPreference = ERROR_SELECT_ONE;
  }

  if (
    values.championPreference &&
    SUCCESS_TRACKS.includes(values.championPreference) &&
    !options.accountIsManaged
  ) {
    errors.championPreference = ERROR_ORG_ACCOUNT_REQUIRED;
  }

  // 6a. Community Forums
  if (values.championPreference === 'community_forums_contributor') {
    const txt = values.howContributeOnline.trim();
    if (!txt) {
      errors.howContributeOnline = ERROR_REQUIRED;
    } else if (txt.length < 150) {
      errors.howContributeOnline = ERROR_MIN_150;
    } else if (txt.length > 300) {
      errors.howContributeOnline = ERROR_MAX_300;
    }
  }

  // 6b. Community Events
  if (values.championPreference === 'community_event_organizer') {
    if (!values.attendedAce) {
      errors.attendedAce = ERROR_REQUIRED;
    }
    if (values.attendedAce === 'yes' && !values.aceExperience.trim()) {
      errors.aceExperience = ERROR_REQUIRED;
    }
    if (!values.fourEventsPerYear) {
      errors.fourEventsPerYear = ERROR_REQUIRED;
    }
    if (!values.activityAndEvent.trim()) {
      errors.activityAndEvent = ERROR_REQUIRED;
    }
  }

  // 6c. Content Creator
  if (values.championPreference === 'community_content_creator') {
    const whyContent = values.whyToCreateContent.trim();
    if (!whyContent) {
      errors.whyToCreateContent = ERROR_REQUIRED;
    } else if (whyContent.length < 150) {
      errors.whyToCreateContent = ERROR_MIN_150;
    } else if (whyContent.length > 300) {
      errors.whyToCreateContent = ERROR_MAX_300;
    }

    if (!values.contentSharingFrequency) {
      errors.contentSharingFrequency = ERROR_REQUIRED;
    }

    if (!values.bestContent1.trim()) {
      errors.bestContent1 = ERROR_REQUIRED;
    }
    if (!values.bestContent2.trim()) {
      errors.bestContent2 = ERROR_REQUIRED;
    }
    if (!values.bestContent3.trim()) {
      errors.bestContent3 = ERROR_REQUIRED;
    }

    if (!values.primarySocialProfile.trim()) {
      errors.primarySocialProfile = ERROR_REQUIRED;
    }

    if (!values.commitSharingContent) {
      errors.commitSharingContent = ERROR_REQUIRED;
    }
  }

  // 6d. Training Builder
  if (values.championPreference === 'success_training_builder') {
    if (!values.trainingFrequency) {
      errors.trainingFrequency = ERROR_REQUIRED;
    }
    if (values.trainingSources.length === 0) {
      errors.trainingSources = ERROR_SELECT_ONE;
    }
  }

  // 6e. CUG Leader
  if (values.championPreference === 'success_cug_leader') {
    if (!values.activeCug) {
      errors.activeCug = ERROR_REQUIRED;
    }
    if (!values.eventsPerYear) {
      errors.eventsPerYear = ERROR_REQUIRED;
    }
  }

  // 6f. CoE Contributor
  if (values.championPreference === 'success_coe_contributor') {
    if (!values.discussInCoE) {
      errors.discussInCoE = ERROR_REQUIRED;
    }
    if (!values.coeFocus) {
      errors.coeFocus = ERROR_REQUIRED;
    }
    if (values.coeFocus === 'Other' && !values.otherExcellence.trim()) {
      errors.otherExcellence = ERROR_REQUIRED;
    }
  }

  // 7. How did you find out about the Atlassian Champions program?
  if (!values.howHeard) {
    errors.howHeard = ERROR_SELECT_ONE;
  }
  if (values.howHeard === 'other' && !values.howHeardOther.trim()) {
    errors.howHeardOther = ERROR_COMPLETE_FIELD;
  }
  if (values.howHeard === 'employee' && !values.howHeardEmployee.trim()) {
    errors.howHeardEmployee = ERROR_COMPLETE_FIELD;
  }
  if (values.howHeard === 'champion' && !values.referrerName.trim()) {
    errors.referrerName = ERROR_COMPLETE_FIELD;
  }

  // 8. Agreements
  if (!values.agreePrivacy) {
    errors.agreePrivacy = ERROR_COMPLETE_FIELD;
  }
  if (!values.agreeNda) {
    errors.agreeNda = ERROR_COMPLETE_FIELD;
  }

  return errors;
};
